import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, GTM_ID, GA_MEASUREMENT_ID } from "@/lib/analytics";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import CookieConsentBanner from "./CookieConsentBanner";

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * AnalyticsProvider - Handles SPA page view tracking with GDPR consent
 *
 * - Shows cookie consent banner until user interacts
 * - Only loads GA4 (gtag.js) and/or GTM after user accepts cookies
 * - Tracks page views on every route change (if consent given)
 */
const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const location = useLocation();
  const isFirstRender = useRef(true);
  const tagsLoaded = useRef(false);
  const { consent } = useCookieConsent();

  // Initialize GA4 / GTM only after consent is given
  useEffect(() => {
    if (tagsLoaded.current || consent !== "accepted") return;

    // Ensure dataLayer exists
    window.dataLayer = window.dataLayer || [];

    const hasGA4 = !!GA_MEASUREMENT_ID && GA_MEASUREMENT_ID.startsWith("G-");
    const hasGTM = !!GTM_ID && GTM_ID !== "GTM-XXXXXXX";

    if (!hasGA4 && !hasGTM) {
      console.info("[Analytics] No GA4 or GTM ID configured");
      return;
    }

    // --- GA4 (gtag.js) ---
    if (hasGA4) {
      const gtagScript = document.createElement("script");
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(gtagScript);

      // gtag stub that pushes to dataLayer
      (window.dataLayer as unknown[]).push(["js", new Date()]);
      (window.dataLayer as unknown[]).push(["config", GA_MEASUREMENT_ID]);

      gtagScript.onload = () => {
        trackPageView(location.pathname + location.search, document.title);
      };
      console.info("[Analytics] GA4 loaded after consent:", GA_MEASUREMENT_ID);
    }

    // --- GTM (optional) ---
    if (hasGTM) {
      window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
      });
      const gtmScript = document.createElement("script");
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
      document.head.appendChild(gtmScript);
      console.info("[Analytics] GTM loaded after consent");
    }

    tagsLoaded.current = true;
  }, [consent, location.pathname, location.search]);

  // Track page views on route change (only if consent given)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (consent !== "accepted") return;

    const hasGA4 = !!GA_MEASUREMENT_ID && GA_MEASUREMENT_ID.startsWith("G-");
    const hasGTM = !!GTM_ID && GTM_ID !== "GTM-XXXXXXX";
    if (!hasGA4 && !hasGTM) return;

    trackPageView(location.pathname + location.search, document.title);
  }, [location.pathname, location.search, consent]);

  return (
    <>
      {children}
      <CookieConsentBanner />
    </>
  );
};

export default AnalyticsProvider;
